import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { LinkComponent, SvgComponent } from '@app/base-components';
import { Entry } from 'contentful';
import { NavigationNode } from 'haba-contentful-components/lib/interfaces/navigation';
import { I18nModule } from 'haba-core';
import { Category, PrefixboxService } from 'haba-emporix';
import { map, Observable, of, startWith, take } from 'rxjs';
import { filterShopCategories, isShopNavigationNode } from '../base/helper';

export interface SsrNavItem {
  name: string;
  url: string;
  maskLink?: boolean;
  shopItem?: boolean;
  isCategory?: boolean;
  children: SsrNavItem[];
}

@Component({
  selector: 'app-ssr-navigation',
  templateUrl: './ssr-navigation.component.html',
  styleUrls: ['./ssr-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, LinkComponent, NgTemplateOutlet, SvgComponent, I18nModule],
})
export class SsrNavigationComponent implements OnChanges {
  @Input() navigationNodes: Entry<NavigationNode>[];

  navItems$: Observable<SsrNavItem[]>;

  constructor(private prefixboxService: PrefixboxService) {}

  ngOnChanges(): void {
    const items = this.toNodeItems(this.navigationNodes, false);
    const shopNode = this.navigationNodes?.find(isShopNavigationNode);

    if (!shopNode) {
      this.navItems$ = of(items);
      return;
    }

    this.navItems$ = this.prefixboxService.getCategoryTree().pipe(
      take(1),
      map((categoryTree) => {
        const shopCategories = this.prefixboxService.removeEmptyCategories(
          filterShopCategories(categoryTree, shopNode)
        );

        return items.map((item) =>
          item.url === shopNode.fields.url
            ? { ...item, children: this.toCategoryItems(shopCategories) }
            : item
        );
      }),
      startWith(items)
    );
  }

  private toNodeItems(nodes: Entry<NavigationNode>[], requireVisible: boolean): SsrNavItem[] {
    return (nodes ?? [])
      .filter((node) => node?.fields?.url && (!requireVisible || node.fields.visible))
      .map((node) => ({
        name: node.fields.name,
        url: node.fields.url,
        maskLink: node.fields.maskLink,
        shopItem: isShopNavigationNode(node),
        children: this.toNodeItems(node.fields.childNavigationNodes, true),
      }));
  }

  private toCategoryItems(categories: Category[]): SsrNavItem[] {
    return (categories ?? []).map((category) => ({
      name: category.name,
      url: category.url,
      isCategory: true,
      children: this.toCategoryItems(category.subcategories),
    }));
  }
}
